/**
 * ExportButton — V14.62 Sprint 20 (Lab).
 *
 * Bouton primaire d'envoi du payload au futur moteur 3D PyTorch.
 *
 * Logique de bridage stricte :
 *   • disabled tant que `scale_m_per_px <= 0` (un mur de 1 cm de haut n'a
 *     aucun sens — la calibration est non-négociable)
 *   • disabled si moins de 2 murs (rien à extruder)
 *
 * Au clic :
 *   • Construit le micro-IFC complet via `toMicroIFC()`
 *   • Toast vert de succès
 *   • console.log du payload (préparation au branchement backend réel)
 */
import { Send, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useGeometryStore } from './useGeometryStore';

const ExportButton = () => {
  const scale = useGeometryStore((s) => s.scale_m_per_px);
  const walls = useGeometryStore((s) => s.walls);
  const toMicroIFC = useGeometryStore((s) => s.toMicroIFC);

  const calibrated = scale > 0;
  const enoughGeometry = walls.length >= 2;
  const isReady = calibrated && enoughGeometry;

  const tooltip = !calibrated
    ? "Calibrez l'échelle avec la Règle (C) avant d'exporter"
    : !enoughGeometry
      ? 'Tracez au moins 2 murs pour générer une géométrie 3D'
      : 'Envoyer le micro-IFC au moteur 3D';

  const handleClick = () => {
    if (!isReady) return;
    const payload = toMicroIFC();
    const stamp = new Date().toISOString();
    console.log(
      `%c[Lab Sprint 20] %cPayload 3D généré ${stamp}`,
      'color:#10b981;font-weight:600',
      'color:inherit',
      payload,
    );
    const bytes = new Blob([JSON.stringify(payload)]).size;
    toast.success('Payload 3D généré avec succès', {
      description: `${payload.walls.length} murs · ${payload.windows.length} fen. · ${payload.doors.length} portes · ${payload.stairs.length} esc. · ${bytes} octets · console.log()`,
      duration: 4500,
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isReady}
      title={tooltip}
      data-testid="export-3d-btn"
      aria-disabled={!isReady}
      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-[12px] font-semibold transition-all ${
        isReady
          ? 'bg-gradient-to-r from-emerald-500 to-teal-400 border-emerald-300 text-emerald-950 hover:from-emerald-400 hover:to-teal-300 hover:shadow-lg hover:shadow-emerald-500/30 cursor-pointer'
          : 'bg-white/5 border-white/10 text-gray-500 cursor-not-allowed'
      }`}
    >
      {isReady ? <Send className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
      Envoyer au Moteur 3D
    </button>
  );
};

export default ExportButton;
